import { logAuthEvent as _logAuthEvent } from '../services/authLoggingService';
import { auth } from './firebaseAuth';
import { AuthEventType } from '../types/logging';

// Safe wrapper for auth logging that handles Firestore permission errors
const logAuthEvent = async (data: Parameters<typeof _logAuthEvent>[0]) => {
  try {
    return await _logAuthEvent(data);
  } catch (error) {
    console.warn("Unable to log auth event to Firestore:", error);
    return "mock-log-id";
  }
};

/**
 * Network monitoring utility for authentication-related requests
 */
class AuthNetworkMonitor {
  private static instance: AuthNetworkMonitor;
  private originalFetch: typeof fetch;
  private isMonitoring: boolean = false;

  private constructor() {
    // Store the original fetch function
    this.originalFetch = window.fetch;
  }

  /**
   * Get singleton instance
   */
  public static getInstance(): AuthNetworkMonitor {
    if (!AuthNetworkMonitor.instance) {
      AuthNetworkMonitor.instance = new AuthNetworkMonitor();
    }
    return AuthNetworkMonitor.instance;
  }

  /**
   * Start monitoring network requests
   */
  public startMonitoring(): void {
    if (this.isMonitoring) return;
    
    // Override the global fetch function
    window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
      // Get the URL string
      const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
      
      // Skip monitoring for AWS SDK requests to avoid "Illegal invocation" errors
      if (this.isAWSRequest(url)) {
        return this.originalFetch.apply(window, [input, init]);
      }
      
      // Check if this is an authentication-related request
      const isAuthRequest = this.isAuthenticationRequest(url);
      
      // If it's an auth request, log the attempt
      if (isAuthRequest) {
        const method = init?.method || 'GET';
        const authEventType = this.getAuthEventType(url, method);
        
        try {
          // Make the original request
          const response = await this.originalFetch.apply(window, [input, init]);
          
          // Log successful request
          if (response.ok && authEventType) {
            const currentUser = auth.currentUser;
            await logAuthEvent({
              userId: currentUser?.uid,
              email: currentUser?.email || this.extractEmailFromRequest(input, init) || 'unknown',
              eventType: authEventType,
              status: 'success',
              details: `Network request: ${method} ${url}`
            });
          }
          
          return response;
        } catch (error) {
          // Log failed request
          if (authEventType) {
            const currentUser = auth.currentUser;
            await logAuthEvent({
              userId: currentUser?.uid,
              email: currentUser?.email || this.extractEmailFromRequest(input, init) || 'unknown',
              eventType: authEventType,
              status: 'failure',
              error: error instanceof Error ? error.message : 'Unknown error',
              details: `Network request failed: ${method} ${url}`
            });
          }
          
          throw error;
        }
      }
      
      // For non-auth requests, just pass through
      return this.originalFetch.apply(window, [input, init]);
    };
    
    this.isMonitoring = true;
    console.log('Auth network monitoring started');
  }

  /**
   * Stop monitoring network requests
   */
  public stopMonitoring(): void {
    if (!this.isMonitoring) return;
    
    // Restore the original fetch function
    window.fetch = this.originalFetch;
    
    this.isMonitoring = false;
    console.log('Auth network monitoring stopped');
  }

  /**
   * Check if a URL is an AWS request or Firestore request
   */
  private isAWSRequest(url: string): boolean {
    // AWS service endpoints and Firestore endpoints
    const awsPatterns = [
      'amazonaws.com',
      's3.',
      'dynamodb.',
      'lambda.',
      'execute-api.',
      'firestore.googleapis.com',
      'googleapis.com'
    ];
    
    return awsPatterns.some(pattern => url.includes(pattern));
  }

  /**
   * Check if a URL is related to authentication
   */
  private isAuthenticationRequest(url: string): boolean {
    // Firebase auth endpoints
    const authPatterns = [
      'identitytoolkit.googleapis.com',
      'securetoken.googleapis.com',
      'firebaseauth',
      'auth/login',
      'auth/register',
      'auth/reset',
      'auth/verify'
    ];
    
    return authPatterns.some(pattern => url.includes(pattern));
  }

  /**
   * Determine the auth event type based on URL and method
   */
  private getAuthEventType(url: string, method: string): AuthEventType | null {
    if (url.includes('accounts:signInWithPassword')) {
      return 'login_success';
    } else if (url.includes('accounts:signUp')) {
      return 'register';
    } else if (url.includes('accounts:sendOobCode') && url.includes('PASSWORD_RESET')) {
      return 'password_reset_request';
    } else if (url.includes('accounts:resetPassword')) {
      return 'password_reset_complete';
    } else if (url.includes('accounts:sendOobCode') && url.includes('VERIFY_EMAIL')) {
      return 'email_verification';
    } else if (url.includes('accounts:update')) {
      return 'profile_update';
    } else if (url.includes('token')) {
      return 'login_success'; // Token refresh is like a re-login
    }
    
    return null;
  }

  /**
   * Try to extract email from request data
   */
  private extractEmailFromRequest(input: RequestInfo | URL, init?: RequestInit): string | null {
    try {
      if (init?.body && typeof init.body === 'string') {
        const data = JSON.parse(init.body);
        return data.email || null;
      }
    } catch (e) {
      // Ignore parsing errors
    }
    return null;
  }
}

export default AuthNetworkMonitor;